import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Scale, Edit, Trash2, Search, Loader2, AlertTriangle, Shield,
  FileText, Globe, Building, Zap, Info
} from 'lucide-react';
import api from '../api';

const RULE_TYPES = [
  { value: 'copyright', label: 'Copyright', icon: FileText, description: 'Detect potential copyright violations' },
  { value: 'trademark', label: 'Trademark', icon: Shield, description: 'Identify trademark usage issues' },
  { value: 'regulatory', label: 'Regulatory', icon: Building, description: 'Ensure regulatory compliance' },
  { value: 'brand_guidelines', label: 'Brand Guidelines', icon: Zap, description: 'Check brand consistency' },
  { value: 'prohibited_content', label: 'Prohibited Content', icon: AlertTriangle, description: 'Flag inappropriate content' },
  { value: 'custom', label: 'Custom Rule', icon: Scale, description: 'Custom compliance rule' }
];

const CATEGORIES = [
  { value: 'gdpr', label: 'GDPR' },
  { value: 'ccpa', label: 'CCPA' },
  { value: 'ftc', label: 'FTC' },
  { value: 'hipaa', label: 'HIPAA' },
  { value: 'financial', label: 'Financial' },
  { value: 'general', label: 'General' },
  { value: 'custom', label: 'Custom' }
];

const SEVERITIES = [
  { value: 'low', label: 'Low', color: 'bg-blue-100 text-blue-700' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700' }
];

export const LegalRulesPage = () => {
  const { hasRole } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rule_type: 'general',
    category: 'general',
    keywords: '',
    patterns: '',
    ai_prompt: '',
    severity: 'medium',
    weight: 1.0,
    examples: []
  });

  useEffect(() => {
    fetchRules();
  }, [typeFilter]);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const params = { is_active: true };
      if (typeFilter !== 'all') params.rule_type = typeFilter;
      
      const res = await api.get('/marketing/legal-rules', { params });
      setRules(res.data || []);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
      toast.error('Failed to load legal rules');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      rule_type: 'general',
      category: 'general',
      keywords: '',
      patterns: '',
      ai_prompt: '',
      severity: 'medium',
      weight: 1.0,
      examples: []
    });
    setEditingRule(null);
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      rule_type: rule.rule_type,
      category: rule.category,
      keywords: rule.keywords?.join(', ') || '',
      patterns: rule.patterns?.join('\n') || '',
      ai_prompt: rule.ai_prompt || '',
      severity: rule.severity,
      weight: rule.weight,
      examples: rule.examples || []
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Rule name is required');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(Boolean),
        patterns: formData.patterns.split('\n').map(p => p.trim()).filter(Boolean),
        weight: parseFloat(formData.weight) || 1.0
      };

      if (editingRule) {
        await api.put(`/marketing/legal-rules/${editingRule.id}`, payload);
        toast.success('Rule updated successfully');
      } else {
        await api.post('/marketing/legal-rules', payload);
        toast.success('Rule created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to deactivate this rule?')) return;

    try {
      await api.delete(`/marketing/legal-rules/${ruleId}`);
      toast.success('Rule deactivated');
      fetchRules();
    } catch (error) {
      toast.error('Failed to deactivate rule');
    }
  };

  const toggleRuleStatus = async (rule) => {
    try {
      await api.put(`/marketing/legal-rules/${rule.id}`, {
        is_active: !rule.is_active
      });
      toast.success(`Rule ${rule.is_active ? 'deactivated' : 'activated'}`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to update rule status');
    }
  };

  const getSeverityBadge = (severity) => {
    const config = SEVERITIES.find(s => s.value === severity) || SEVERITIES[1];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getRuleTypeIcon = (type) => {
    const config = RULE_TYPES.find(t => t.value === type);
    if (config) {
      const Icon = config.icon;
      return <Icon className="h-4 w-4" />;
    }
    return <Scale className="h-4 w-4" />;
  };

  const filteredRules = rules.filter(rule =>
    rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rule.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!hasRole(['admin'])) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="p-8 text-center">
          <Scale className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">Only administrators can manage legal rules.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="legal-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Scale className="h-6 w-6" />
            Legal Compliance Rules
          </h1>
          <p className="text-muted-foreground">
            Configure AI-powered legal review rules for marketing content
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-rule-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRule ? 'Edit Legal Rule' : 'Create Legal Rule'}</DialogTitle>
              <DialogDescription>
                Define rules for AI-powered content compliance checking
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rule Name *</Label>
                  <Input
                    placeholder="e.g., No Competitor Mentions"
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rule Type</Label>
                  <Select value={formData.rule_type} onValueChange={(v) => setFormData(p => ({ ...p, rule_type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RULE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Describe what this rule checks for..."
                  value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Severity</Label>
                  <Select value={formData.severity} onValueChange={(v) => setFormData(p => ({ ...p, severity: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map(sev => (
                        <SelectItem key={sev.value} value={sev.value}>{sev.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Weight</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="5"
                    value={formData.weight}
                    onChange={(e) => setFormData(p => ({ ...p, weight: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Keywords to Flag
                  <Info className="h-3 w-3 text-muted-foreground" />
                </Label>
                <Input
                  placeholder="competitor, lawsuit, guarantee (comma-separated)"
                  value={formData.keywords}
                  onChange={(e) => setFormData(p => ({ ...p, keywords: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  Content containing these keywords will be flagged
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Regex Patterns
                  <Info className="h-3 w-3 text-muted-foreground" />
                </Label>
                <Textarea
                  placeholder="One pattern per line, e.g.:\n\\$\\d+.*guaranteed\n\\d+%.*return"
                  value={formData.patterns}
                  onChange={(e) => setFormData(p => ({ ...p, patterns: e.target.value }))}
                  rows={3}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Regular expressions for advanced pattern matching
                </p>
              </div>

              <div className="space-y-2">
                <Label>Custom AI Prompt (Optional)</Label>
                <Textarea
                  placeholder="Additional instructions for the AI reviewer..."
                  value={formData.ai_prompt}
                  onChange={(e) => setFormData(p => ({ ...p, ai_prompt: e.target.value }))}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Custom instructions for AI-powered review (used when AI is enabled)
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Scale className="h-5 w-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-900">How Legal Review Works</p>
              <p className="text-sm text-purple-700 mt-1">
                When a campaign enters the Legal Review stage, the AI analyzes all content against these rules.
                Each rule has keywords and patterns to detect. The overall score must reach 90% (configurable) to pass.
                Failed reviews require content revisions before proceeding.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search rules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Rule Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {RULE_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Rules Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredRules.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Scale className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No legal rules configured</h3>
            <p className="text-muted-foreground mb-4">
              Add rules to enable AI-powered legal compliance checking
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Rule
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rule</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Keywords</TableHead>
                <TableHead>Weight</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{rule.name}</p>
                      {rule.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{rule.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getRuleTypeIcon(rule.rule_type)}
                      <span className="capitalize text-sm">{rule.rule_type?.replace('_', ' ')}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="uppercase text-xs">{rule.category}</Badge>
                  </TableCell>
                  <TableCell>{getSeverityBadge(rule.severity)}</TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {rule.keywords?.length || 0} keywords
                    </span>
                  </TableCell>
                  <TableCell>{rule.weight}x</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(rule)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(rule.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default LegalRulesPage;
